import * as Yup from 'yup';

import { combineSchemas } from 'common/utils';
import { Fields, PK, SelectOption, StatusOption } from 'common/types';

export type SeriesFields = {
  title: string;
  status: StatusOption[];
  description: string;
  short_description: string;
  punctation: SelectOption<number>[];
  content_goals: SelectOption<PK>[];
  start_date: Date | null;
  end_date: Date | null;
  is_challenge: boolean;
  challenge_start_date: Date | null;
  is_featured: boolean;
  is_inspiration: boolean;
};

export const fields: Fields<SeriesFields> = {
  title: {
    label: 'Title',
    name: 'title',
    schema: Yup.string().required(),
  },
  short_description: {
    label: 'Short description',
    name: 'short_description',
    schema: Yup.string().required(),
  },
  description: {
    label: 'Description',
    name: 'description',
    schema: Yup.string().required(),
  },
  status: {
    label: 'Status',
    name: 'status',
    schema: Yup.array().required(),
  },
  content_goals: {
    label: 'Categories',
    name: 'content_goals',
    schema: Yup.array().required(),
  },
  punctation: {
    label: 'Points',
    name: 'punctation',
    schema: Yup.array().required(),
  },
  start_date: {
    label: 'From',
    name: 'start_date',
  },
  end_date: {
    label: 'To',
    name: 'end_date',
  },
  is_challenge: {
    label: 'Is this serie a challenge?',
    name: 'is_challenge',
  },
  challenge_start_date: {
    label: 'Start date',
    name: 'challenge_start_date',
  },
  is_featured: {
    label: 'Is this content featured?',
    name: 'is_featured',
  },
  is_inspiration: {
    label: 'Is this content an inspiration?',
    name: 'is_inspiration',
  },
};

export const validationSchema = Yup.object().shape<SeriesFields>(combineSchemas(fields));

export type SeriesTagsFields = {
  amount: number;
  amount_unit: SelectOption<string>;
  category: SelectOption<string>[];
  frequency: SelectOption<string>;
  habit: SelectOption<string>[];
  subcategory: SelectOption<string>[];
  term: SelectOption<string>;
};

export const tagsFields: Fields<SeriesTagsFields> = {
  amount: {
    label: 'Amount',
    name: 'amount',
    schema: Yup.number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .test('Is positive?', 'Amount must be greater than 0.', (value) => {
        return typeof value === 'object' || (typeof value === 'number' && value > 0);
      }),
  },
  amount_unit: {
    label: 'Amount unit',
    name: 'amount_unit',
    schema: Yup.array()
      .nullable()
      .when('amount', {
        is: (amount) => {
          return typeof amount === 'number';
        },
        then: Yup.array()
          .nullable()
          .required('Amount and Unit fields must be set together'),
      }),
  },
  category: {
    label: 'Category',
    name: 'category',
    schema: Yup.array().required('Category is a required field'),
  },

  frequency: {
    label: 'Frequency',
    name: 'frequency',
    schema: Yup.number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .test('Is positive?', 'Frequency must be greater than 0.', (value) => {
        return typeof value === 'object' || (typeof value === 'number' && value > 0);
      }),
  },
  habit: {
    label: 'Habit',
    name: 'habit',
    schema: Yup.array().required('Habit is a required field'),
  },

  subcategory: {
    label: 'Subcategory',
    name: 'subcategory',
    schema: Yup.array().required('Subcategory is a required field'),
  },
  term: {
    label: 'Term',
    name: 'term',
    schema: Yup.array().nullable(),
  },
};

export const seriesTagsValidationSchema = Yup.object().shape<SeriesTagsFields>(
  combineSchemas(tagsFields),
);
